import { formatInTimeZone } from 'date-fns-tz';
import { useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import { Child } from '../../../../services/endpoints/people/user';
import { useGetAllWaitlistsQuery } from '../../../../services/endpoints/schedule/waitlist';
import { getCurrentUser } from '../../../../services/helper';
import { GetQueryParams } from '../../../../types/api.type';
import { Course } from '../../../../types/course.type';
import { Waitlist } from '../../../../types/waitlist.type';
import {
  getTimezoneAbbreviation,
  isMissingSessionTime,
  storedSessionDateTimeToInstant
} from '../../../../utils/dateUtils';
import { loginPathWithReturnTo } from '../../../../utils/returnTo';

interface CourseRegisterButtonProps {
  course: Course;
  addChild: () => void;
  registerCourse: () => void;
  userChildren?: Child[];
}

function isCourseSoldOut(course: Course): boolean {
  const capacity = +course.enrollment?.split('/')[1];
  const enrollments = +course.enrollment?.split('/')[0];
  return enrollments >= capacity;
}

// Registration window times are FancyTime-encoded like session times, so they
// must be decoded against the location's timezone rather than read as venue-local
// wall clock. See storedSessionDateTimeToInstant.
function venueLocalDateTime(
    datePart: string | null | undefined,
    timePart: string | null | undefined,
    timezone: string
): Date | null {
  return storedSessionDateTimeToInstant(datePart, timePart, timezone);
}

function getRegistrationCloseDateTime(course: Course, timezone: string): Date | null {
  const explicitClose = venueLocalDateTime(
    course.registration_ends_on,
    course.registration_ends_at,
    timezone
  );
  if (explicitClose) return explicitClose;

  const lessons = course.lessons ?? [];
  if (!lessons.length) return null;

  const final = [...lessons].sort((a, b) => b.date.localeCompare(a.date))[0];
  // Virtual class times are stored as the 00:00:00 placeholder rather than null,
  // so isMissingSessionTime rather than ?? decides whether to inherit.
  return venueLocalDateTime(
      final.date,
      isMissingSessionTime(final.starts_at) ? course.starts_at : final.starts_at,
      timezone
  );
}

function formatRegistrationDateTime(date: Date, timezone: string): string {
  const formatted = formatInTimeZone(date, timezone, "MMMM d, yyyy 'at' h:mm a");
  const abbrev = getTimezoneAbbreviation(timezone);
  return abbrev ? `${formatted} ${abbrev}` : formatted;
}

const CourseRegisterButton: React.FC<CourseRegisterButtonProps> = ({
  course,
  addChild,
  userChildren,
  registerCourse
}) => {
  const currentUser = getCurrentUser();
  const navigate = useNavigate();
  const venueTimezone =
      ((course.venue as any)?.location?.timezone as string) ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = new Date();
  const todayInVenue = formatInTimeZone(now, venueTimezone, 'yyyy-MM-dd');

  const waitlistFilters: GetQueryParams = {
    scope: 'all',
    data: {
      filterBy: [{ field: 'course_id', operator: 'equals', value: course.id }],
      page: 1,
      entries: 100
    }
  };
  const { data: waitlists } = useGetAllWaitlistsQuery(waitlistFilters, {
    skip: !currentUser || !course.id,
    refetchOnMountOrArgChange: true
  });
  const childIds = new Set(
    (userChildren ?? [])
      .map((c) => c.id)
      .filter((id): id is number => typeof id === 'number')
  );
  const hasInvite = ((waitlists?.results as Waitlist[] | undefined) ?? []).some((w) => {
    const status =
      typeof w.status === 'number'
        ? ['pending', 'invited', 'enrolled', 'expired', 'denied', 'archived'][w.status]
        : String(w.status ?? '').toLowerCase();
    return childIds.has(Number(w.child_id)) && status === 'invited';
  });

  let buttonLabel = 'Register Now';
  const isSoldOut = isCourseSoldOut(course);

  // Session has ended if all lessons are in the past.
  const hasUpcomingLesson = (course.lessons ?? []).some((l) => l.date >= todayInVenue);
  const hasEnded = !hasUpcomingLesson;

  // Registration open time: combined start date/time, or open-now if none set.
  const opensAt = venueLocalDateTime(
    course.registration_starts_on,
    course.registration_starts_at,
    venueTimezone
  );
  const notYetOpen = opensAt ? now.getTime() < opensAt.getTime() : false;

  // Registration close time: combined end date/time, or the final class start if none set.
  const closesAt = getRegistrationCloseDateTime(course, venueTimezone);
  const isClosed = closesAt ? now.getTime() > closesAt.getTime() : false;

  // Precedence: ended -> not yet open -> closed -> login/children -> invited overrides sold out.
  if (hasEnded) {
    buttonLabel = 'Session Ended';
  } else if (notYetOpen) {
    buttonLabel = `Opens ${formatRegistrationDateTime(opensAt!, venueTimezone)}`;
  } else if (isClosed) {
    buttonLabel = 'Registration Closed';
  } else if (!currentUser) {
    buttonLabel = 'Log In to Register';
  } else if (userChildren?.length === 0) {
    buttonLabel = 'Add a Child To Register';
  } else if (hasInvite) {
    buttonLabel = 'Register Now';
  } else if (isSoldOut) {
    buttonLabel = 'Join Waitlist';
  }

  // Button is actionable only when registration is genuinely open.
  const registrationOpen = !hasEnded && !notYetOpen && !isClosed;

  return (
    <>
      <span style={{ color: '#B81F69' }}>
        {hasEnded ? (
          <>This session has already ended. Please look for upcoming sessions to register for.</>
        ) : notYetOpen ? (
          <>Registration for this course opens on {formatRegistrationDateTime(opensAt!, venueTimezone)}.</>
        ) : isClosed ? (
          <>Registration for this course is closed.</>
        ) : !currentUser ? (
          <>Please log in to register or join the waitlist for this session.</>
        ) : userChildren?.length === 0 ? (
          <>
            You don't have any children tied to your account to enroll. Add at least one child using
            the button below and then you can enroll them in this class and more!
          </>
        ) : hasInvite ? (
          <>A spot is available for your child — register now to secure it.</>
        ) : isSoldOut ? (
          <>
            Sold Out! <span className="fw-bold">JOIN THE WAITLIST</span>
          </>
        ) : null}
      </span>

      {/* Hide the button entirely when not open or closed (incl. waitlist) per spec. */}
      {!notYetOpen && !isClosed && (
        <div className="course-register-btn-wrapper">
          <SimpleButton
            type="button"
            className="mt-3 course-register-btn"
            variant="primary"
            disabled={hasEnded}
            onClick={() => {
              if (hasEnded) return;
              if (!currentUser) {
                const returnTo = `${window.location.pathname}${window.location.search}`;
                navigate(loginPathWithReturnTo(returnTo));
                return;
              }
              if (userChildren?.length === 0) {
                addChild();
                return;
              }
              if (registrationOpen) {
                registerCourse();
              }
            }}
          >
            {buttonLabel}
          </SimpleButton>
        </div>
      )}
    </>
  );
};

export default CourseRegisterButton;
